import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Button,
  Empty,
  Input,
  Select,
  Spin,
  Tooltip,
  Pagination,
  message,
} from "antd";
import {
  DollarOutlined,
  FileTextOutlined,
  PrinterOutlined,
  SearchOutlined,
  UserOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

import { api } from "../../../../../config/api";
import { useConfigQuery } from "../../../../../queries/useConfig";
import "./GerenciamentoCaixas.css";

const { Option } = Select;

const normalizarLista = (data, chaves = []) => {
  if (Array.isArray(data)) return data;

  for (const chave of chaves) {
    if (Array.isArray(data?.[chave])) return data[chave];
  }

  const embedded = data?._embedded;

  if (embedded && typeof embedded === "object") {
    for (const valor of Object.values(embedded)) {
      if (Array.isArray(valor)) return valor;
    }
  }

  return [];
};

const formatarDataHora = (data) => {
  if (!data) return "--";

  const valor = new Date(data);

  if (Number.isNaN(valor.getTime())) {
    return String(data);
  }

  return valor.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const escaparHtml = (valor) =>
  String(valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatarValorRelatorio = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const obterTextoSobraFalta = (valor) => {
  const numero = Number(valor || 0);

  if (numero === 0) {
    return "";
  }

  const valorFormatado = formatarValorRelatorio(
    Math.abs(numero)
  );

  if (numero < 0) {
    return `SOBRA DE CAIXA: ${valorFormatado}`;
  }

  return `FALTA DE CAIXA: ${valorFormatado}`;
};

const obterDataMovimentacao = (movimentacao) =>
  movimentacao?.data ||
  movimentacao?.dataHora ||
  movimentacao?.dataMovimentacao ||
  movimentacao?.dataHoraMovimentacao ||
  movimentacao?.createdAt ||
  movimentacao?.criadoEm ||
  movimentacao?.criacao ||
  null;

const formatarDataInput = (data) => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");

  return `${ano}-${mes}-${dia}`;
};

const obterPeriodoInicial = () => {
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(ontem.getDate() - 1);

  return {
    dataInicio: formatarDataInput(ontem),
    dataFim: formatarDataInput(hoje),
  };
};

const GerenciamentoCaixas = ({ onClose }) => {
  const { data: config } = useConfigQuery();

  const accessToken = window.localStorage.getItem("accessToken");
  const operadorLogado = window.localStorage.getItem("operador");

  const idLoja = config?.idLoja || config?.restaurante?.id || null;

  const periodoInicial = useMemo(() => obterPeriodoInicial(), []);

  const [busca, setBusca] = useState("");
  const [idCaixaSelecionado, setIdCaixaSelecionado] = useState("");
  const [dataInicio, setDataInicio] = useState(periodoInicial.dataInicio);
  const [dataFim, setDataFim] = useState(periodoInicial.dataFim);

  const [caixas, setCaixas] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [sessoes, setSessoes] = useState([]);

  const [loadingCaixas, setLoadingCaixas] = useState(false);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(false);
  const [loadingSessoes, setLoadingSessoes] = useState(false);
  const [imprimindoSessao, setImprimindoSessao] = useState(null);

  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);

  const getOptions = useCallback(
    (params = {}) => ({
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
      params,
    }),
    [accessToken]
  );

  const obterIdCaixa = (sessao) =>
    sessao?.idCaixa ||
    sessao?.id_caixa ||
    sessao?.caixa?.id ||
    sessao?.caixaId ||
    "";

  const obterCaixaDaSessao = useCallback(
    (sessao) => {
      const idCaixa = String(obterIdCaixa(sessao) || "");

      return caixas.find((caixa) => String(caixa?.id || "") === idCaixa);
    },
    [caixas]
  );

  const obterDescricaoCaixa = useCallback(
    (sessao) => {
      const caixa = obterCaixaDaSessao(sessao);

      return (
        sessao?.caixaDescricao ||
        sessao?.descricaoCaixa ||
        sessao?.caixa?.descricao ||
        caixa?.descricao ||
        obterIdCaixa(sessao) ||
        "--"
      );
    },
    [obterCaixaDaSessao]
  );

  const getResponsavelId = useCallback(
    (responsavel) =>
      responsavel?.id ||
      responsavel?.idUsuario ||
      responsavel?.usuario?.id ||
      responsavel?.userId,
    []
  );

  const getResponsavelNome = useCallback(
    (responsavel) =>
      responsavel?.nome ||
      responsavel?.nomeUsuario ||
      responsavel?.usuario?.nome ||
      responsavel?.name ||
      responsavel?.email ||
      getResponsavelId(responsavel) ||
      "Usuário",
    [getResponsavelId]
  );

  const getResponsavelEmail = useCallback(
    (responsavel) =>
      responsavel?.email ||
      responsavel?.usuario?.email ||
      responsavel?.login ||
      "",
    []
  );

  const nomeResponsavelPorId = useCallback(
    (usuarioId) => {
      if (!usuarioId) return "Não informado";

      const responsavel = responsaveis.find(
        (item) => String(getResponsavelId(item)) === String(usuarioId)
      );

      return responsavel ? getResponsavelNome(responsavel) : usuarioId;
    },
    [getResponsavelId, getResponsavelNome, responsaveis]
  );

  const obterResponsavel = useCallback(
    (sessao) => nomeResponsavelPorId(sessao?.idUsuario),
    [nomeResponsavelPorId]
  );

  const carregarResponsaveis = useCallback(async () => {
    if (!idLoja || !accessToken) return;

    setLoadingResponsaveis(true);

    try {
      const response = await axios.get(
        `${api.techfood}/v1/restaurantes/${idLoja}/responsaveis`,
        getOptions()
      );

      const dados =
        response?.data?._embedded?.responsaveis ||
        response?.data?._embedded?.Responsavel ||
        response?.data?._embedded?.usuarios ||
        response?.data?.content ||
        response?.data ||
        [];

      setResponsaveis(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar responsáveis:", error);
      setResponsaveis([]);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível carregar os usuários da loja."
      );
    } finally {
      setLoadingResponsaveis(false);
    }
  }, [accessToken, getOptions, idLoja]);

  const carregarCaixas = useCallback(async () => {
    if (!idLoja || !accessToken) return;

    setLoadingCaixas(true);

    try {
      const response = await axios.get(
        `${api.techspdv}/api/caixas`,
        getOptions({ idLoja })
      );

      const lista = normalizarLista(response.data, ["caixas", "content"]);
      setCaixas(lista);
    } catch (error) {
      console.error("Erro ao carregar caixas:", error);
      setCaixas([]);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível carregar os caixas da loja."
      );
    } finally {
      setLoadingCaixas(false);
    }
  }, [accessToken, getOptions, idLoja]);

  const buscarSessoes = useCallback(async (pagina = 0) => {
    if (!idLoja || !accessToken) {
      message.warning("Loja ou autenticação não disponível.");
      return;
    }

    if (!dataInicio || !dataFim) {
      message.warning("Informe a data inicial e a data final.");
      return;
    }

    if (dataInicio > dataFim) {
      message.warning("A data inicial não pode ser maior que a data final.");
      return;
    }

    setLoadingSessoes(true);

    try {
      const params = {
        idLoja,
        dataInicio,
        dataFim,
        page: pagina,
      };

      if (busca.trim()) {
        params.busca = busca.trim();
      }

      if (idCaixaSelecionado) {
        params.idCaixa = idCaixaSelecionado;
      }

      const response = await axios.get(
        `${api.techspdv}/api/sessoes-caixa/historico`,
        getOptions(params)
      );

      const conteudo = Array.isArray(response.data?.content)
        ? response.data.content
        : [];

      setSessoes(conteudo);
      setPaginaAtual(Number(response.data?.number || 0));
      setTotalRegistros(Number(response.data?.totalElements || 0));
      setTotalPaginas(Number(response.data?.totalPages || 0));
      setBuscaRealizada(true);
    } catch (error) {
      console.error("Erro ao carregar histórico de sessões:", error);

      setSessoes([]);
      setPaginaAtual(0);
      setTotalRegistros(0);
      setTotalPaginas(0);
      setBuscaRealizada(true);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível carregar o histórico de sessões de caixa."
      );
    } finally {
      setLoadingSessoes(false);
    }
  }, [
    accessToken,
    busca,
    dataFim,
    dataInicio,
    getOptions,
    idCaixaSelecionado,
    idLoja,
  ]);

  const executarBusca = () => {
    setPaginaAtual(0);
    buscarSessoes(0);
  };

  const alterarPagina = (pagina) => {
    buscarSessoes(pagina - 1);
  };

  useEffect(() => {
    carregarCaixas();
    carregarResponsaveis();
  }, [carregarCaixas, carregarResponsaveis]);

  const abrirJanelaImpressao = (titulo, conteudo) => {
    const janela = window.open("", "_blank", "width=420,height=720");

    if (!janela) {
      message.warning(
        "O navegador bloqueou a janela de impressão. Libere pop-ups para imprimir o relatório."
      );
      return;
    }

    janela.document.open();
    janela.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8" />
          <title>${escaparHtml(titulo)}</title>
          <style>
            @page { size: 80mm auto; margin: 2mm; }
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; background: #fff; color: #000; }
            body {
              width: 72mm;
              margin: 0 auto;
              font-family: "Courier New", Courier, monospace;
              font-size: 10px;
              line-height: 1.25;
            }
            .cupom { width: 100%; padding: 1mm 0; }
            .centro { text-align: center; }
            .titulo { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
            .subtitulo { font-size: 11px; font-weight: 700; margin-bottom: 4px; }
            .linha { border-top: 1px dashed #000; margin: 5px 0; }
            .linha-dupla { border-top: 2px solid #000; margin: 5px 0; }
            .info { margin: 1px 0; word-break: break-word; }
            .forma { margin-top: 7px; font-weight: 700; text-transform: uppercase; }
            .item { display: flex; justify-content: space-between; gap: 6px; margin: 1px 0; }
            .item span:first-child { min-width: 0; overflow-wrap: anywhere; }
            .item span:last-child { white-space: nowrap; text-align: right; }
            .movimento { padding: 4px 0; border-bottom: 1px dashed #000; }
            .movimento-cabecalho {
              display: flex;
              justify-content: space-between;
              gap: 5px;
              font-weight: 700;
            }
            .observacao { margin-top: 2px; font-size: 9px; overflow-wrap: anywhere; }
            .espaco-duplo { height: 20px; }
            .diferenca-caixa {
              margin: 7px 0 5px;
              text-align: center;
              font-size: 12px;
              font-weight: 700;
              text-transform: uppercase;
            }
            .rodape { margin-top: 6px; text-align: center; font-size: 9px; }
            @media print { html, body { width: 72mm; } }
          </style>
        </head>
        <body>
          <div class="cupom">${conteudo}</div>
          <script>
            window.onload = function () {
              setTimeout(function () { window.print(); }, 250);
            };
            window.onafterprint = function () { window.close(); };
          </script>
        </body>
      </html>
    `);

    janela.document.close();
  };

  const carregarDadosRelatorio = async (sessao) => {
    if (!sessao?.id || !idLoja) {
      throw new Error("Sessão ou loja não informada.");
    }

    const response = await axios.get(
      `${api.techspdv}/api/sessoes-caixa/${sessao.id}/movimentacoes`,
      getOptions({ idLoja })
    );

    const movimentacoes = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data?.movimentacoes)
        ? response.data.movimentacoes
        : [];

    const indicadores =
      !Array.isArray(response.data) && response.data?.indicadores
        ? response.data.indicadores
        : {};

    const agrupado = {};

    movimentacoes.forEach((movimentacao) => {
      const forma = String(
        movimentacao?.formaDePagamento || "DINHEIRO"
      ).toUpperCase();

      if (!agrupado[forma]) {
        agrupado[forma] = {
          formaPagamento:
            forma === "PIX" ? "Pix" : forma === "DINHEIRO" ? "Dinheiro" : forma,
          depositos: 0,
          vendas: 0,
          saques: 0,
          cancelamentos: 0,
          saidas: 0,
          saldo: 0,
        };
      }

      const item = agrupado[forma];
      const operacao = String(movimentacao?.operacao || "").toUpperCase();
      const tipo = String(movimentacao?.tipo || "").toUpperCase();
      const valor = Number(movimentacao?.valor || 0);
      const valorAbsoluto = Math.abs(valor);

      switch (operacao) {
        case "ABERTURA":
        case "ENTRADA_INICIAL":
        case "ENTRADA":
        case "DEPOSITO":
        case "DEPÓSITO":
          item.depositos += valorAbsoluto;
          break;
        case "VENDA":
          item.vendas += valorAbsoluto;
          break;
        case "SAQUE":
          item.saques += valorAbsoluto;
          break;
        case "CANCELAMENTO":
        case "DEVOLUÇÃO":
        case "DEVOLUCAO":
          item.cancelamentos += valorAbsoluto;
          break;
        case "SANGRIA":
        case "PAGAMENTO":
        case "TROCO":
        case "FECHAMENTO":
          item.saidas += valorAbsoluto;
          break;
        default:
          break;
      }

      if (tipo === "S" || valor < 0) {
        item.saldo -= valorAbsoluto;
      } else {
        item.saldo += valorAbsoluto;
      }
    });

    return {
      movimentacoes,
      indicadores,
      resumoPorForma: Object.values(agrupado),
    };
  };

  const imprimirResumo = async (sessao) => {
    setImprimindoSessao(`${sessao.id}_resumo`);

    try {
      const {
        resumoPorForma,
        indicadores
      } = await carregarDadosRelatorio(sessao);

      const gerarBlocoIndicador = (
        titulo,
        descricao,
        indicador
      ) => {
        const quantidade = Number(indicador?.quantidade || 0);
        const valor = Number(indicador?.valor || 0);

        return `
        <div class="forma">${escaparHtml(titulo)}</div>

        <div class="item">
          <span>Quantidade de ${escaparHtml(descricao)}</span>
          <strong>${quantidade}</strong>
        </div>

        <div class="item">
          <span>Valor de ${escaparHtml(descricao)}</span>
          <strong>${formatarValorRelatorio(valor)}</strong>
        </div>

        <div class="linha"></div>
      `;
      };
      const blocosIndicadores = [
        gerarBlocoIndicador(
          'VENDAS',
          'vendas',
          indicadores?.vendas
        ),

        gerarBlocoIndicador(
          'PEDIDOS',
          'pedidos',
          indicadores?.pedidos
        ),

        gerarBlocoIndicador(
          'SERVIÇOS',
          'serviços',
          indicadores?.servicos
        ),

        gerarBlocoIndicador(
          'VENDAS CANCELADAS',
          'vendas canceladas',
          indicadores?.vendasCanceladas
        ),
      ].join('');

      const dados = Array.isArray(resumoPorForma) ? resumoPorForma : [];

      const totais = dados.reduce(
        (acc, item) => {
          acc.depositos += Number(item?.depositos || 0);
          acc.vendas += Number(item?.vendas || 0);
          acc.saques += Number(item?.saques || 0);
          acc.cancelamentos += Number(item?.cancelamentos || 0);
          acc.saidas += Number(item?.saidas || 0);
          acc.saldo += Number(item?.saldo || 0);
          return acc;
        },
        {
          depositos: 0,
          vendas: 0,
          saques: 0,
          cancelamentos: 0,
          saidas: 0,
          saldo: 0,
        }
      );

      const linhasFormas = dados
        .map(
          (item) => `
            <div class="forma">${escaparHtml(item?.formaPagamento || "--")}</div>
            <div class="item"><span>Depositos</span><span>${formatarValorRelatorio(
            item?.depositos
          )}</span></div>
            <div class="item"><span>Vendas</span><span>${formatarValorRelatorio(
            item?.vendas
          )}</span></div>
            <div class="item"><span>Saques</span><span>${Number(item?.saques || 0) > 0 ? "- " : ""
            }${formatarValorRelatorio(item?.saques)}</span></div>
            <div class="item"><span>Cancelamentos</span><span>${Number(item?.cancelamentos || 0) > 0 ? "- " : ""
            }${formatarValorRelatorio(item?.cancelamentos)}</span></div>
            <div class="item"><span>Saidas</span><span>
             ${item.saidas > 0 ? '- ' : ''}${formatarValorRelatorio(item.saidas)} 
            </span></div>
            <div class="item"><strong>Saldo</strong><strong>${formatarValorRelatorio(
              item?.saldo
            )}</strong></div>
            <div class="linha"></div>
          `
        )
        .join("");

      const nomeEmpresa =
        config?.restaurante?.nome ||
        config?.restaurante?.nomeFantasia ||
        "TECHS PDV";

      const textoSobraFalta = obterTextoSobraFalta(totais.saldo);

      const conteudo = `
        <div class="centro titulo">${escaparHtml(nomeEmpresa)}</div>
        <div class="centro subtitulo">RESUMO DE CAIXA</div>
        <div class="linha-dupla"></div>
        <div class="info"><strong>Caixa:</strong> ${escaparHtml(
        obterDescricaoCaixa(sessao)
      )}</div>
        <div class="info"><strong>Sessao:</strong> ${escaparHtml(
        sessao?.id || "--"
      )}</div>
        <div class="info"><strong>Operador:</strong> ${escaparHtml(
        obterResponsavel(sessao) || operadorLogado || "--"
      )}</div>
        <div class="info"><strong>Abertura:</strong> ${escaparHtml(
        formatarDataHora(sessao?.abertura)
      )}</div>
        <div class="info"><strong>Fechamento:</strong> ${escaparHtml(
        formatarDataHora(sessao?.fechamento)
      )}</div>
        <div class="info"><strong>Emissao:</strong> ${escaparHtml(
        formatarDataHora(new Date())
      )}</div>
        <div class="linha-dupla"></div>
        ${linhasFormas || '<div class="centro">Nenhum movimento encontrado.</div>'}
        <div class="forma">TOTAIS</div>
        <div class="item"><span>Depositos</span><span>${formatarValorRelatorio(
        totais.depositos
      )}</span></div>
        <div class="item"><span>Vendas</span><span>${formatarValorRelatorio(
        totais.vendas
      )}</span></div>
        <div class="item"><span>Saques</span><span>${totais.saques > 0 ? "- " : ""
        }${formatarValorRelatorio(totais.saques)}</span></div>
        <div class="item"><span>Cancelamentos</span><span>${totais.cancelamentos > 0 ? "- " : ""
        }${formatarValorRelatorio(totais.cancelamentos)}</span></div>
        <div class="item"><span>Saidas</span><span>
          ${totais.saidas > 0 ? '- ' : ''}${formatarValorRelatorio(totais.saidas)} 
        </span></div>
        <div class="item"><strong>Saldo geral</strong><strong>${formatarValorRelatorio(
          totais.saldo
        )}</strong></div>
        <div class="linha-dupla"></div>
 ${textoSobraFalta
          ? `
      <div class="espaco-duplo"></div>
      <div class="diferenca-caixa">
        ${escaparHtml(textoSobraFalta)}
      </div>
        <div class="espaco-duplo"></div>
      <div class="linha-dupla"></div>
    `
          : ""
        }


${blocosIndicadores}

<div class="linha-dupla"></div>
    
 <div class="rodape">Relatorio gerado pelo controle de caixa</div>
      `;

      abrirJanelaImpressao("Resumo de Caixa", conteudo);
    } catch (error) {
      console.error("Erro ao imprimir resumo:", error);
      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Não foi possível imprimir o resumo da sessão."
      );
    } finally {
      setImprimindoSessao(null);
    }
  };

  const imprimirMovimentacoes = async (sessao) => {
    setImprimindoSessao(`${sessao.id}_movimentacoes`);

    try {
      const { movimentacoes } = await carregarDadosRelatorio(sessao);

      const movimentos = Array.isArray(movimentacoes)
        ? [...movimentacoes]
        : [];

      // Mais antigo em cima e mais novo embaixo, igual ao relatório atual.
      movimentos.sort((a, b) => {
        const dataA = new Date(obterDataMovimentacao(a) || 0).getTime();
        const dataB = new Date(obterDataMovimentacao(b) || 0).getTime();

        if (Number.isNaN(dataA) && Number.isNaN(dataB)) return 0;
        if (Number.isNaN(dataA)) return 1;
        if (Number.isNaN(dataB)) return -1;
        return dataA - dataB;
      });

      const linhas = movimentos
        .map((movimentacao, index) => {
          const tipo = String(movimentacao?.tipo || "").toUpperCase();
          const valorOriginal = Number(movimentacao?.valor || 0);
          const sinal = valorOriginal < 0 || tipo === "S" ? "-" : "+";
          const valorAbsoluto = Math.abs(valorOriginal);

          const observacao = movimentacao?.observacao
            ? `<div class="observacao">Obs.: ${escaparHtml(
              movimentacao.observacao
            )}</div>`
            : "";

          return `
            <div class="movimento">
              <div class="movimento-cabecalho">
                <span>${String(index + 1).padStart(2, "0")} - ${escaparHtml(
            movimentacao?.operacao || "--"
          )}</span>
              </div>
              <div class="item">
                <span>Data/Hora:</span>
                <strong>${escaparHtml(
            formatarDataHora(obterDataMovimentacao(movimentacao))
          )}</strong>
              </div>
              <div class="item">
                <span>${escaparHtml(
            movimentacao?.formaDePagamento || "--"
          )}</span>
                <strong>${sinal} ${formatarValorRelatorio(
            valorAbsoluto
          )}</strong>
              </div>
              ${observacao}
            </div>
          `;
        })
        .join("");

      const totalEntradas = movimentos.reduce((total, movimentacao) => {
        const tipo = String(movimentacao?.tipo || "").toUpperCase();
        const valor = Number(movimentacao?.valor || 0);
        return tipo === "E" || valor > 0 ? total + Math.abs(valor) : total;
      }, 0);

      const totalSaidas = movimentos.reduce((total, movimentacao) => {
        const tipo = String(movimentacao?.tipo || "").toUpperCase();
        const valor = Number(movimentacao?.valor || 0);
        return tipo === "S" || valor < 0 ? total + Math.abs(valor) : total;
      }, 0);

      const nomeEmpresa =
        config?.restaurante?.nome ||
        config?.restaurante?.nomeFantasia ||
        "TECHS PDV";

      const totalMovimentado = totalEntradas - totalSaidas;
      const textoSobraFalta = obterTextoSobraFalta(totalMovimentado);

      const conteudo = `
        <div class="centro titulo">${escaparHtml(nomeEmpresa)}</div>
        <div class="centro subtitulo">MOVIMENTAÇÃO DE CAIXA</div>
        <div class="linha-dupla"></div>
        <div class="info"><strong>Caixa:</strong> ${escaparHtml(
        obterDescricaoCaixa(sessao)
      )}</div>
        <div class="info"><strong>Sessao:</strong> ${escaparHtml(
        sessao?.id || "--"
      )}</div>
        <div class="info"><strong>Operador:</strong> ${escaparHtml(
        obterResponsavel(sessao) || operadorLogado || "--"
      )}</div>
        <div class="info"><strong>Abertura:</strong> ${escaparHtml(
        formatarDataHora(sessao?.abertura)
      )}</div>
        <div class="info"><strong>Fechamento:</strong> ${escaparHtml(
        formatarDataHora(sessao?.fechamento)
      )}</div>
        <div class="info"><strong>Emissao:</strong> ${escaparHtml(
        formatarDataHora(new Date())
      )}</div>
        <div class="linha-dupla"></div>
        ${linhas || '<div class="centro">Nenhuma movimentacao encontrada.</div>'}
        <div class="linha-dupla"></div>
        <div class="item"><strong>Movimentações</strong><strong>${movimentos.length
        }</strong></div>
        <div class="item"><strong>Total entradas</strong><strong>${formatarValorRelatorio(
          totalEntradas
        )}</strong></div>
        <div class="item"><strong>Total saidas</strong><strong>${totalSaidas > 0 ? "- " : ""
        }${formatarValorRelatorio(totalSaidas)}</strong></div>
        <div class="item"><strong>Total</strong><strong>${formatarValorRelatorio(
          totalMovimentado
        )}</strong></div>
        <div class="linha-dupla"></div>
 ${textoSobraFalta
          ? `
      <div class="espaco-duplo"></div>
      <div class="diferenca-caixa">
        ${escaparHtml(textoSobraFalta)}
      </div>
          <div class="espaco-duplo"></div>
      <div class="linha-dupla"></div>
    `
          : ""
        }
        <div class="rodape">Relatorio gerado pelo controle de caixa</div>
      `;

      abrirJanelaImpressao("Movimentações de Caixa", conteudo);
    } catch (error) {
      console.error("Erro ao imprimir movimentações:", error);
      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Não foi possível imprimir as movimentações da sessão."
      );
    } finally {
      setImprimindoSessao(null);
    }
  };

  const caixasOrdenados = useMemo(
    () =>
      [...caixas].sort((a, b) =>
        String(a?.descricao || "").localeCompare(
          String(b?.descricao || ""),
          "pt-BR"
        )
      ),
    [caixas]
  );

  return (
    <div className="gerenciamento-caixas-page">
      <div className="gerenciamento-caixas-card">
        <div className="gerenciamento-caixas-header">
          <div>
            <h2>Resumos e Movimentações de Caixa</h2>
            <span className="gerenciamento-caixas-subtitulo">
              Consulte sessões já fechadas e imprima os relatórios da sessão escolhida.
            </span>
          </div>

          {onClose && (
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={onClose}
              className="btn-voltar-caixa"
            >
              Voltar
            </Button>
          )}
        </div>

        <div className="gerenciamento-caixas-filtros">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Id da sessão ou nome do caixa..."
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            allowClear
            className="input-busca-caixa"
          />

          <Select
            value={idCaixaSelecionado}
            onChange={setIdCaixaSelecionado}
            className="select-caixa-filtro"
            loading={loadingCaixas || loadingResponsaveis}
            placeholder="Todos..."
          >
            <Option value="">Todos...</Option>

            {caixasOrdenados.map((caixa) => {
              const responsavel = nomeResponsavelPorId(caixa?.idUsuario);
              const mostrarResponsavel =
                responsavel &&
                responsavel !== "Não informado" &&
                responsavel !== caixa?.idUsuario;

              return (
                <Option key={caixa.id} value={String(caixa.id)}>
                  {caixa.descricao || caixa.nome || caixa.id}
                  {mostrarResponsavel ? ` - ${responsavel}` : ""}
                </Option>
              );
            })}
          </Select>

          <div className="periodo-caixa">
            <span className="periodo-label">De</span>
            <Input
              type="date"
              value={dataInicio}
              onChange={(event) => setDataInicio(event.target.value)}
              className="input-data-caixa"
            />

            <span className="periodo-label">Até</span>
            <Input
              type="date"
              value={dataFim}
              onChange={(event) => setDataFim(event.target.value)}
              className="input-data-caixa"
            />
          </div>

          <Tooltip title="Buscar sessões fechadas">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              className="btn-buscar-caixa"
              onClick={executarBusca}
              loading={loadingSessoes}
            >
              Buscar
            </Button>
          </Tooltip>
        </div>

        <div className="sessao-grid sessao-grid-header">
          <div>Caixa</div>
          <div>Responsável</div>
          <div>Abertura</div>
          <div>Fechamento</div>
          <div className="acoes-titulo">Ações</div>
        </div>

        <Spin spinning={loadingSessoes}>
          {!buscaRealizada ? (
            <div className="gerenciamento-caixas-empty">
              <Empty description="Preencha os filtros e clique em Buscar" />
            </div>
          ) : sessoes.length === 0 ? (
            <div className="gerenciamento-caixas-empty">
              <Empty description="Nenhuma sessão fechada encontrada para os filtros informados" />
            </div>
          ) : (
            sessoes.map((sessao) => (
              <div key={sessao.id} className="sessao-grid sessao-grid-row">
                <div className="caixa-info">
                  <div className="caixa-icone">
                    <DollarOutlined />
                  </div>

                  <div>
                    <strong>{obterDescricaoCaixa(sessao)}</strong>
                    <span title={sessao.id}>Sessão: {sessao.id}</span>
                  </div>
                </div>

                <div className="responsavel-info">
                  <div className="responsavel-icone">
                    <UserOutlined />
                  </div>
                  <span>{obterResponsavel(sessao)}</span>
                </div>

                <div className="data-sessao">
                  <strong>{formatarDataHora(sessao.abertura)}</strong>
                </div>

                <div className="data-sessao">
                  <strong>{formatarDataHora(sessao.fechamento)}</strong>
                </div>

                <div className="acoes-caixa">
                  <Tooltip title="Imprimir resumo de caixa">
                    <Button
                      type="text"
                      className="acao-relatorio acao-resumo"
                      icon={<FileTextOutlined />}
                      loading={imprimindoSessao === `${sessao.id}_resumo`}
                      disabled={Boolean(imprimindoSessao)}
                      onClick={() => imprimirResumo(sessao)}
                    />
                  </Tooltip>

                  <Tooltip title="Imprimir movimentações da sessão">
                    <Button
                      type="text"
                      className="acao-relatorio acao-movimentacoes"
                      icon={<PrinterOutlined />}
                      loading={
                        imprimindoSessao === `${sessao.id}_movimentacoes`
                      }
                      disabled={Boolean(imprimindoSessao)}
                      onClick={() => imprimirMovimentacoes(sessao)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </Spin>

        {buscaRealizada && totalRegistros > 0 && (
          <div className="paginacao-sessoes">
            <span className="paginacao-info">
              {totalRegistros} registro{totalRegistros === 1 ? "" : "s"}
              {totalPaginas > 0 ? ` • ${totalPaginas} página${totalPaginas === 1 ? "" : "s"}` : ""}
            </span>

            <Pagination
              current={paginaAtual + 1}
              pageSize={10}
              total={totalRegistros}
              showSizeChanger={false}
              onChange={alterarPagina}
              disabled={loadingSessoes}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GerenciamentoCaixas;
